import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Add,
  ExpandMore,
  MenuBook,
  Refresh,
  Restaurant,
  Grass,
  Settings,
} from '@mui/icons-material';
import { toast } from 'sonner';
import {
  ComplianceItem,
  ComplianceStatus,
  DSCommunitySchema,
  ResourceSchema,
  ResourceType,
} from '../../data/types';
import { useLoadCommunityResourcesOnly } from '../../hooks/resourcehooks';
import { useLoadComplianceItems } from '../../hooks/compliancehooks';
import {
  ComplianceResourceOption,
  countActionNeeded,
  filterResourcesByType,
  formatComplianceLocationLabel,
  getOfferedComplianceResourceOptions,
  hasCompleteComplianceLocation,
  resolveComplianceLocation,
  shouldShowComplianceOnboarding,
  tracksKitchenComplianceRequirements,
} from '../../utils/complianceHelpers';
import LoadingScreen from '../../pages/shared/LoadingScreen';
import ActionNeededBadge from './ActionNeededBadge';
import ComplianceLegalDrawer from './ComplianceLegalDrawer';
import ComplianceUploadModal from './ComplianceUploadModal';
import OnboardingWizard from './OnboardingWizard';

type CompliancePageProps = {
  community: DSCommunitySchema;
  communityId: string;
};

const resourceIcons: Partial<Record<ResourceType, React.ElementType>> = {
  kitchen: Restaurant,
  documentation: MenuBook,
  farm: Grass,
};

/** Setup checklist tab content — resource compliance tracking and uploads. */
export const CompliancePage = ({ community, communityId }: CompliancePageProps) => {
  const navigate = useNavigate();
  const offeredOptions = useMemo(
    () => getOfferedComplianceResourceOptions(community.preferences),
    [community.preferences]
  );
  const [selectedResourceType, setSelectedResourceType] = useState<ResourceType | undefined>(
    () => getOfferedComplianceResourceOptions(community.preferences)[0]?.id
  );
  const [expandedResourceId, setExpandedResourceId] = useState<string | null>(null);
  const [legalItem, setLegalItem] = useState<ComplianceItem | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(() =>
    shouldShowComplianceOnboarding(communityId, community)
  );

  const resolvedResourceType =
    offeredOptions.find((option) => option.id === selectedResourceType)?.id ??
    offeredOptions[0]?.id;
  const selectedOption =
    offeredOptions.find((option) => option.id === resolvedResourceType) ??
    offeredOptions[0];

  const { resources, isLoadingResources } = useLoadCommunityResourcesOnly(communityId);
  const filteredResources = useMemo(
    () =>
      resolvedResourceType
        ? filterResourcesByType(resources, resolvedResourceType)
        : [],
    [resources, resolvedResourceType]
  );

  useEffect(() => {
    document.title = 'HomeCook | Setup Checklist';
  }, []);

  useEffect(() => {
    if (
      offeredOptions.length > 0 &&
      selectedResourceType &&
      !offeredOptions.some((option) => option.id === selectedResourceType)
    ) {
      setSelectedResourceType(offeredOptions[0].id);
      setExpandedResourceId(null);
    }
  }, [offeredOptions, selectedResourceType]);

  useEffect(() => {
    if (filteredResources.length === 1) {
      setExpandedResourceId(filteredResources[0].id || null);
    }
  }, [filteredResources]);

  if (showOnboarding) {
    return (
      <OnboardingWizard
        community={community}
        communityId={communityId}
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => setShowOnboarding(false)}
      />
    );
  }

  return (
    <>
      <div className="flex flex-row h-[75px] w-full border-t border-[#E6E6E6] bg-white">
        <p className="mx-[24px] my-auto max-w-2xl font-open-sans text-[16px] text-[#4D4D4D]/70">
        Finish setup and track resource compliance. 
        {/* Complete uploads and renew items when they xpire. */}
          {/* Track kitchen compliance for each resource by city, county, state, and zip code. Upload */}
          {/* documents for HomeCook staff review. */}
        </p>
        {offeredOptions.length > 0 && selectedOption && (
          <div className="ml-auto mr-[24px] my-auto">
            <ResourceTypeDropdown
              value={selectedOption.id}
              options={offeredOptions}
              onChange={(resourceType) => {
                setSelectedResourceType(resourceType);
                setExpandedResourceId(null);
              }}
            />
          </div>
        )}
      </div>

      <div className="w-11/12 mx-auto my-4">
        <section className="overflow-hidden rounded-xl border border-[#E9E9E9] bg-white">
          {offeredOptions.length === 0 ? (
            <EmptyOfferSettingsPrompt onOpenSettings={() => navigate('/settings')} />
          ) : isLoadingResources ? (
            <LoadingScreen bgColor="white" height="h-[240px]" />
          ) : filteredResources.length === 0 && selectedOption ? (
            <ResourceEmptyState
              option={selectedOption}
              onCreate={() => navigate(selectedOption.createPath)}
            />
          ) : (
            <ul className="divide-y divide-[#E9E9E9]">
              {filteredResources.map((resource) => (
                <ResourceComplianceRow
                  key={resource.id}
                  resource={resource}
                  community={community}
                  expanded={expandedResourceId === resource.id}
                  onToggle={() =>
                    setExpandedResourceId((current) =>
                      current === resource.id ? null : resource.id || null
                    )
                  }
                  onOpenLegal={setLegalItem}
                />
              ))}
            </ul>
          )}
        </section>
      </div>

      {legalItem && (
        <ComplianceLegalDrawer
          item={legalItem}
          region={resolveComplianceLocation(community, undefined).state || 'your area'}
          onClose={() => setLegalItem(null)}
        />
      )}
    </>
  );
};

type ResourceComplianceRowProps = {
  resource: ResourceSchema;
  community: DSCommunitySchema;
  expanded: boolean;
  onToggle: () => void;
  onOpenLegal: (item: ComplianceItem) => void;
};

const ResourceComplianceRow = ({
  resource,
  community,
  expanded,
  onToggle,
  onOpenLegal,
}: ResourceComplianceRowProps) => {
  const resourceId = resource.id || '';
  const resourceType = resource.resource_type || 'kitchen';
  const tracksCompliance = tracksKitchenComplianceRequirements(resourceType);
  const location = useMemo(
    () => resolveComplianceLocation(community, resource),
    [community, resource]
  );
  const [uploadItem, setUploadItem] = useState<ComplianceItem | null>(null);
  const {
    items,
    isLoading,
    uploadComplianceDocument,
    isUploading,
    syncCompliance,
    isSyncing,
  } = useLoadComplianceItems({
    resourceId,
    resourceType,
    location,
    autoSync: tracksCompliance && hasCompleteComplianceLocation(community, resource),
  });

  const actionCount = countActionNeeded(items, resourceType);
  const ResourceIcon = resourceIcons[resourceType] || Restaurant;

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-4 px-6 py-5 text-left transition-colors hover:bg-[#fafafa]"
      >
        {resource.logo ? (
          <img
            src={resource.logo}
            alt={resource.name}
            className="h-14 w-14 shrink-0 rounded-2xl border border-[#E9E9E9] object-contain"
          />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#EFF8FA]">
            <ResourceIcon className="text-primary-400" />
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-raleway text-lg font-semibold text-[#013847]">{resource.name}</h3>
            <ResourceStatusBadge active={Boolean(resource.active)} />
            {tracksCompliance && <ActionNeededBadge count={actionCount} />}
          </div>
          <p className="mt-1 font-open-sans text-sm text-[#4D4D4D]">
            {formatComplianceLocationLabel(resource)}
          </p>
          {tracksCompliance ? (
            <p className="mt-1 font-open-sans text-sm text-[#808080]">
              {items.length} {resourceType} compliance item{items.length === 1 ? '' : 's'}
            </p>
          ) : (
            <p className="mt-1 font-open-sans text-sm text-[#808080]">
              Compliance requirements are tracked for kitchen spaces only.
            </p>
          )}
        </div>

        <ExpandMore
          className={`shrink-0 text-[#737373] transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && tracksCompliance && (
        <RequirementList
          items={items}
          isLoading={isLoading}
          isSyncing={isSyncing}
          onSelectItem={setUploadItem}
          onOpenLegal={onOpenLegal}
          onSync={async () => {
            try {
              const result = await syncCompliance(true);
              const count = result?.items?.length ?? items.length;
              toast.success(
                count > 0
                  ? `Requirements refreshed (${count} item${count === 1 ? '' : 's'}).`
                  : 'Requirements are up to date.'
              );
            } catch {
              toast.error('Could not refresh requirements. Please try again.');
            }
          }}
        />
      )}

      {expanded && !tracksCompliance && (
        <p className="border-t border-[#E9E9E9] bg-[#fafafa] px-6 py-6 font-open-sans text-sm text-[#4D4D4D]">
          Setup checklist requirements and action items are available for kitchen spaces. Switch to
          Kitchen Spaces to review location-specific compliance for each kitchen resource.
        </p>
      )}

      {uploadItem && (
        <ComplianceUploadModal
          item={uploadItem}
          isUploading={isUploading}
          onClose={() => setUploadItem(null)}
          onOpenLegal={() => onOpenLegal(uploadItem)}
          onUpload={async (file) => {
            if (!uploadItem.id) {
              toast.error('Upload failed. Missing compliance item id.');
              return;
            }
            const updated = await uploadComplianceDocument({
              itemId: uploadItem.id,
              file,
            });
            if (updated) {
              toast.success('Document uploaded - awaiting HomeCook staff review.');
            } else {
              toast.error('Upload failed. Please try again.');
            }
          }}
        />
      )}
    </li>
  );
};

type RequirementListProps = {
  items: ComplianceItem[];
  isLoading: boolean;
  isSyncing?: boolean;
  onSelectItem: (item: ComplianceItem) => void;
  onOpenLegal: (item: ComplianceItem) => void;
  onSync: () => void | Promise<void>;
};

const RequirementList = ({
  items,
  isLoading,
  isSyncing = false,
  onSelectItem,
  onOpenLegal,
  onSync,
}: RequirementListProps) => {
  if (isLoading) {
    return (
      <div className="border-t border-[#E9E9E9] bg-[#fafafa] px-6 py-8">
        <LoadingScreen bgColor="white" height="h-[120px]" />
      </div>
    );
  }

  return (
    <div className="border-t border-[#E9E9E9] bg-[#fafafa]">
      <div className="flex items-center justify-between gap-3 border-b border-[#E9E9E9]/60 px-6 py-3">
        <p className="font-open-sans text-xs text-[#808080]">
          Requirements are based on this resource&apos;s city, county, state, and zip code.
        </p>
        <button
          type="button"
          onClick={() => void onSync()}
          disabled={isSyncing}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E9E9E9] bg-white px-3 py-1.5 font-open-sans text-xs font-medium text-[#013847] transition-colors hover:bg-[#f8f8f8] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Refresh
            className={isSyncing ? 'animate-spin' : ''}
            sx={{ fontSize: 14 }}
          />
          {isSyncing ? 'Syncing…' : 'Sync requirements'}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="px-6 py-6 font-open-sans text-sm text-[#4D4D4D]">
          No requirements generated yet. Confirm location details, then sync requirements.
        </p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item.id || item.item_key} className="border-b border-[#E9E9E9]/60 last:border-b-0">
              <div className="flex w-full items-center justify-between gap-4 px-6 py-4 pl-10">
                <button
                  type="button"
                  onClick={() => onSelectItem(item)}
                  className="min-w-0 flex-1 text-left transition-colors hover:opacity-90"
                >
                  <p className="font-raleway text-sm font-semibold text-[#111111]">{item.title}</p>
                  <p className="mt-1 line-clamp-2 font-open-sans text-sm text-[#4D4D4D]">
                    {item.description}
                  </p>
                  {item.expiryDate && (
                    <p className="mt-1 font-open-sans text-sm text-[#808080]">
                      Expires {item.expiryDate}
                    </p>
                  )}
                </button>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <StatusBadge status={item.status} />
                  <button
                    type="button"
                    onClick={() => onOpenLegal(item)}
                    className="font-open-sans text-xs text-primary-200 underline"
                  >
                    Why required?
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ResourceStatusBadge = ({ active }: { active: boolean }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-0.5 font-open-sans text-[13px] font-medium ${
      active ? 'bg-[#E8F5EC] text-[#0F973D]' : 'bg-[#F3F4F6] text-[#737373]'
    }`}
  >
    {active ? 'Active' : 'Inactive'}
  </span>
);

const StatusBadge = ({ status }: { status: ComplianceStatus }) => {
  const config = {
    pending: {
      label: 'Pending upload',
      className: 'bg-[#FFF4E5] text-[#B45309]',
    },
    pending_verification: {
      label: 'Awaiting review',
      className: 'bg-[#EFF8FA] text-[#5DB6CE]',
    },
    approved: {
      label: 'Approved',
      className: 'bg-[#E8F5EC] text-[#0F973D]',
    },
    expired: {
      label: 'Action needed',
      className: 'bg-[#FFF4E5] text-[#B45309]',
    },
  }[status];

  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 font-open-sans text-[13px] font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
};

/** Prompt partners to choose checklist types in Settings when none are offered. */
const EmptyOfferSettingsPrompt = ({
  onOpenSettings,
}: {
  onOpenSettings: () => void;
}) => (
  <div className="flex flex-col items-center px-6 py-16 text-center">
    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EFF8FA]">
      <Settings className="text-primary-400" sx={{ fontSize: 36 }} />
    </span>
    <h3 className="mt-5 font-raleway text-xl font-semibold text-[#013847]">
      Choose resources to offer
    </h3>
    <p className="mt-2 max-w-md font-open-sans text-sm text-[#4D4D4D]">
      Select kitchen spaces, documentation, or fresh food in Settings to use the setup checklist.
    </p>
    <button
      type="button"
      onClick={onOpenSettings}
      className="mt-6 inline-flex h-11 items-center gap-2 rounded-lg bg-primary-200 px-5 font-open-sans text-sm font-medium text-white"
    >
      Open Settings
    </button>
  </div>
);

const ResourceEmptyState = ({
  option,
  onCreate,
}: {
  option: ComplianceResourceOption;
  onCreate: () => void;
}) => {
  const Icon = resourceIcons[option.id] || Restaurant;

  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EFF8FA]">
        <Icon className="text-primary-400" sx={{ fontSize: 36 }} />
      </span>
      <h3 className="mt-5 font-raleway text-xl font-semibold text-[#013847]">
        No {option.label.toLowerCase()} yet
      </h3>
      <p className="mt-2 max-w-md font-open-sans text-sm text-[#4D4D4D]">
        Create a {option.label.toLowerCase()} resource in Resources to add it to your setup
        checklist.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-6 inline-flex h-11 items-center gap-2 rounded-lg bg-primary-200 px-5 font-open-sans text-sm font-medium text-white"
      >
        <Add sx={{ fontSize: 18 }} />
        {option.createLabel}
      </button>
    </div>
  );
};

const ResourceTypeDropdown = ({
  value,
  options,
  onChange,
}: {
  value: ResourceType;
  options: ComplianceResourceOption[];
  onChange: (resourceType: ResourceType) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full max-w-[220px]">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        className="flex h-11 w-full items-center justify-between rounded-lg border border-[#E9E9E9] bg-white px-4 font-open-sans text-sm text-[#111111]"
      >
        <span>{selected?.label ?? 'Select resource'}</span>
        <ExpandMore
          className={`text-[#737373] transition-transform ${open ? 'rotate-180' : ''}`}
          sx={{ fontSize: 18 }}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-20 w-full min-w-[220px] overflow-hidden rounded-lg border border-[#E9E9E9] bg-white shadow-[0_8px_24px_rgba(1,56,71,0.12)]">
          <ul className="max-h-64 overflow-y-auto py-1">
            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left font-open-sans text-sm transition-colors hover:bg-[#f8f8f8] ${
                    value === option.id
                      ? 'bg-[#f8f8f8] font-medium text-[#111111]'
                      : 'text-[#4D4D4D]'
                  }`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CompliancePage;
